import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { JobPositionShift } from '@restworld/utility-types';
import { itemListProps } from '@restworld/utils-common';

import { aggregateWorkdayFn } from './job-shifts.utils';
import { JobItemListType } from './job-shifts.types';

export const useJobShiftsList = ({ daysRequiredPerWeek, daysRequiredPerWeekNumber, shifts }: JobItemListType) => {
  const fullRestTimeItemList = useFullRestTimeItemList({
    daysRequiredPerWeek,
    daysRequiredPerWeekNumber,
  });
  const shiftList = useShifts({ shifts });
  const itemList: itemListProps[] = useMemo(
    () => [...fullRestTimeItemList, ...shiftList],
    [fullRestTimeItemList, shiftList]
  );
  return itemList;
};
const useShifts = ({ shifts }: JobItemListType) => {
  const shiftList = useMemo(() => {
    const list: itemListProps[] = [];
    if (shifts?.length) {
      shifts.map((data, indx) => {
        const {
          days_per_week_number: daysPerWeekNumber,
          days_per_week: daysPerWeek,
          category_other: categoryOther = '',
          hours = [],
          category_var: categoryVar,
        }: JobPositionShift = data;
        let subTitle = categoryOther;
        if (hours.length) {
          subTitle = hours.reduce((previousValue, currentValue, currentIndex) => {
            const { start_at: startAt, end_at: endAt } = currentValue;
            const isLastIndex = hours.length > 1 && currentIndex === hours.length - 1;
            const returnString = `${previousValue} ${isLastIndex ? 'e' : ''}${
              currentIndex > 0 ? ' d' : 'D'
            }alle ${startAt} alle ${endAt}`;
            return returnString;
          }, '');
        } else if (categoryVar?.label && categoryVar?.key !== 'other') subTitle = categoryVar?.label;
        const label = `${
          daysPerWeek?.length
            ? `${aggregateWorkdayFn(daysPerWeek)}`
            : daysPerWeekNumber
            ? `${daysPerWeekNumber} giorni su 7`
            : categoryVar?.label || categoryOther
        }`;
        const shouldBeVisiblePartTime = Boolean(hours.length || categoryVar?.label || categoryOther);
        const returnShift: itemListProps = {
          key: indx.toString(),
          label,
          icon: 'PartTimeClock' as const,
          subTitle,
        };
        if (shouldBeVisiblePartTime) list.push(returnShift);
        return returnShift;
      });
    }
    return list;
  }, [shifts]);
  return shiftList;
};
const useFullRestTimeItemList = ({ daysRequiredPerWeek, daysRequiredPerWeekNumber }: JobItemListType) => {
  const { t } = useTranslation('job-position-page');
  const shouldBeVisibleFullRest = Boolean(daysRequiredPerWeek?.length || daysRequiredPerWeekNumber);
  const getSubTitleForFullRest = useCallback(
    (isInverted?: boolean) =>
      daysRequiredPerWeek?.length
        ? aggregateWorkdayFn(daysRequiredPerWeek, isInverted)
        : `${
            daysRequiredPerWeekNumber
              ? `${isInverted ? 7 - daysRequiredPerWeekNumber : daysRequiredPerWeekNumber} giorni su 7`
              : ''
          }`,
    [daysRequiredPerWeek, daysRequiredPerWeekNumber]
  );
  const fullRestTimeItemList: itemListProps[] = useMemo(
    () => [
      ...(shouldBeVisibleFullRest
        ? [
            {
              key: 'gioni-di-lavoro',
              label: t('job_shift.full_time'),
              icon: 'FullTimeClock' as const,
              subTitle: getSubTitleForFullRest(false),
            },
          ]
        : []),
      ...(shouldBeVisibleFullRest
        ? [
            {
              key: 'giorni-di-riposo',
              label: t('job_shift.part_time'),
              icon: 'RestTimeSofa' as const,
              subTitle: getSubTitleForFullRest(true),
            },
          ]
        : []),
    ],
    [getSubTitleForFullRest, shouldBeVisibleFullRest, t]
  );
  return fullRestTimeItemList;
};
